import { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import {
  Column,
  Row,
  Page,
  Loader,
  Spinner,
  Empty,
  Heading,
  Text,
  Spacer,
  Toggle,
  Button,
  Flex,
  Tabs,
  ProgressBar,
  Message,
} from '@oliasoft-open-source/react-ui-library';
import { FeatureFlags } from '@oliasoft/feature-flags';
import {
  getCampaigns,
  campaignModalOpened,
  learningCurveModalUpdated,
  kpiModalUpdated,
  updateLearningCurveFlag,
  getSimulation,
  addSimulationStatus,
  getCampaignSimulation,
  getCampaignResults,
  campaignsCleanup,
  runCampaignTotalSimulation,
  updateIsRecalculating,
  getCampaignTimeTracker,
  campaignSelected,
  getCampaignDetails,
} from 'store/entities/campaigns/campaigns.ts';
import { CampaignsList } from 'views/campaigns/campaigns.list';
import { DeleteCampaignModal } from 'views/campaigns/delete-campaign.modal';
import { CampaignModal } from 'views/campaigns/campaign.modal';
import { Summary } from 'views/campaigns/summary/summary';
import { OperationsSequence } from 'views/campaigns/operations-sequence/operations-sequence';
import { TimeTracker } from 'src/views/campaigns/time-tracker/time-tracker';
import translations from 'src/internationalisation/translation-map.json';
import { LearningCurveModal } from 'views/campaigns/learning-curve.modal';
import { KpiModal } from 'views/campaigns/kpi/kpi.modal';
import { selectPrimaryCurrency } from 'src/store/entities/company-settings/selectors';
import { CompareEstimatesDrawer } from 'views/compare-estimates/drawer/compare-estimates.drawer';
import { useSocket, WEBSOCKET_EVENTS } from 'src/common/socket-contex/socket-context';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { CampaignStatus, CampaignTabIndex } from 'src/enums/campaigns';
import { probabilities } from 'src/enums/simulations';
import { FeatureFlagName } from 'src/enums/feature-flags';
import { useProgress } from 'src/common/progress/use-progress';
import {
  selectActiveCampaign,
  selectActiveEstimates,
  selectCampaignDesigns,
  selectCampaignList,
  selectCampaignModalVisible,
  selectCampaignTotal,
  selectConceptDesigns,
  selectIsFetchingCampaign,
  selectIsFetchingCampaignResults,
  selectIsGeneratingCampaignEstimates,
  selectIsRecalculatingCampaigns,
  selectKpiModalVisible,
  selectLearningCurveModalVisible,
} from 'src/store/entities/campaigns/selectors.ts';
import { convertToKebabCase } from 'client/common/utils/string-utils';
import { CampaignInputTab } from './campaign-tabs/campaign-input-tab';
import { getLoaderText, isCampaignSynced } from './utils/utils';

const Campaigns = ({
  isFetching,
  campaignModalVisible,
  campaignModalOpened,
  learningCurveModalVisible,
  learningCurveModalUpdated,
  kpiModalUpdated,
  kpiModalVisible,
  activeCampaign,
  getCampaigns,
  list,
  primaryCurrency,
  updateLearningCurveFlag,
  designs,
  conceptDesigns,
  getSimulation,
  addSimulationStatus,
  visibleEstimates,
  activeCampaignTotal,
  getCampaignSimulation,
  getCampaignResults,
  isFetchingCampaignResults,
  campaignsCleanup,
  runCampaignTotalSimulation,
  updateIsRecalculating,
  getCampaignTimeTracker,
  isRecalculating,
  isGeneratingEstimates,
  campaignSelected,
  getCampaignDetails,
}) => {
  const { t } = useTranslation();
  const { company } = useParams();
  const { socket } = useSocket();
  const isCampaignTimeTrackerActive = FeatureFlags.isActive(FeatureFlagName.CampaignTimeTracker);

  const [selectedId, setSelectedId] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [dataEntity, setDataEntity] = useState(0);
  const [probability, setProbability] = useState(probabilities.P10);
  const [deleteCampaignModalVisible, setDeleteCampaignModalVisible] = useState(false);
  const onClickDeleteCampaign = (id) => {
    setSelectedId(id);
    setDeleteCampaignModalVisible(true);
  };

  const location = useLocation();
  const campaignToSelect = useRef(location.state?.campaignId ?? null);
  const detailsRequested = useRef(false);

  useEffect(() => {
    const id = campaignToSelect.current;
    if (!id) {
      return;
    }
    const target = list.find((c) => c.campaignId === id);
    if (!target || list.some((c) => c.active)) {
      return;
    }
    campaignSelected(id);
    if (!detailsRequested.current) {
      getCampaignDetails(id);
      detailsRequested.current = true;
    }
  }, [list]);

  useEffect(() => {
    if (company) {
      getCampaigns(company);
    }
    socket.on(WEBSOCKET_EVENTS.notification_message, (m) => addSimulationStatus(m.notification));
    return () => {
      campaignsCleanup();
    };
  }, []);

  useEffect(() => {
    Promise.all(
      visibleEstimates.map((estimate) => {
        if (estimate?.simulation?.status === 'Finished' && !estimate?.simulation?.fetched && !estimate?.isFetching) {
          return getSimulation(estimate.projectId);
        }
        return null;
      }),
    );
  }, [visibleEstimates]);

  useEffect(() => {
    if (
      activeCampaignTotal?.simulation?.status === 'Finished' &&
      !activeCampaignTotal?.simulation?.fetched &&
      !activeCampaignTotal?.isFetching &&
      activeCampaignTotal?.campaignId
    ) {
      getCampaignSimulation(activeCampaignTotal?.campaignId);
    }
  }, [activeCampaignTotal?.simulation]);

  useEffect(() => {
    if (activeCampaign?.status !== CampaignStatus.Actual && activeTabIndex === CampaignTabIndex.TimeTracker) {
      setActiveTabIndex(CampaignTabIndex.Input);
    }
  }, [activeCampaign?.status, activeTabIndex]);

  const { campaignTotal, estimates, isSynced } = activeCampaign || {};
  const isActiveCampaignActual = activeCampaign?.status === CampaignStatus.Actual;
  const showCampaignTimeTracker = isCampaignTimeTrackerActive && isActiveCampaignActual;
  const isCampaignOutOfSync = !!campaignTotal && !isCampaignSynced(activeCampaign);

  const shouldFetchResults =
    activeCampaign &&
    !isFetchingCampaignResults &&
    (activeCampaign.detailsFetched === false ||
      (campaignTotal === undefined &&
        (!designs || designs.length === 0) &&
        (estimates?.length > 0 || activeCampaign.estimatesCount > 0)));

  const handleOnTabChange = (e) => {
    const nextTabIndex = +e.target.value;
    if (nextTabIndex === CampaignTabIndex.TimeTracker && showCampaignTimeTracker && activeCampaign?.campaignId) {
      setActiveTabIndex(nextTabIndex);
      getCampaignTimeTracker(activeCampaign.campaignId);
      return;
    }

    if (
      (nextTabIndex === CampaignTabIndex.Result || nextTabIndex === CampaignTabIndex.Operations) &&
      shouldFetchResults
    ) {
      setActiveTabIndex(+e.target.value);
      getCampaignResults(activeCampaign.campaignId);
    } else {
      setActiveTabIndex(+e.target.value);
    }
  };

  const showProgressBar = isRecalculating || isGeneratingEstimates;

  const progress = useProgress(
    activeCampaignTotal?.simulation?.status,
    updateIsRecalculating,
    isRecalculating,
    isGeneratingEstimates,
  );

  const renderContent = () => {
    if (activeTabIndex === CampaignTabIndex.Input) {
      return null;
    }

    if (showCampaignTimeTracker && activeTabIndex === CampaignTabIndex.TimeTracker) {
      return renderTimeTracker();
    }

    if (activeCampaign?.isFetchingDetails || isFetchingCampaignResults) {
      return (
        <Loader text={t(translations.fetching)} theme="light" height="90%">
          <Spinner dark />
        </Loader>
      );
    }

    if (activeCampaign?.campaignTotal || isGeneratingEstimates) {
      if (showProgressBar) {
        return renderLoader();
      }
      if (activeTabIndex === CampaignTabIndex.Operations) {
        return renderOperationsSequence();
      }
      if (activeTabIndex === CampaignTabIndex.Result) {
        return renderSummary();
      }
    }
    return renderEmpty();
  };

  const renderLoader = () => (
    <Loader
      text={getLoaderText(activeCampaignTotal?.simulation?.message, isGeneratingEstimates)}
      theme="light"
      width="100%"
      height="90%"
    >
      <ProgressBar percentage={progress} showProgressColors width="300px" />
    </Loader>
  );

  const renderSummary = () => (
    <Summary
      activeCampaign={activeCampaign}
      kpiModalUpdated={kpiModalUpdated}
      primaryCurrency={primaryCurrency}
      activeCampaignTotal={activeCampaignTotal}
      updateIsRecalculating={updateIsRecalculating}
      setDataEntity={setDataEntity}
      dataEntity={dataEntity}
      probability={probability}
      setProbability={setProbability}
    />
  );

  const renderTimeTracker = () => <TimeTracker />;

  const renderOperationsSequence = () => <OperationsSequence />;

  const renderEmpty = () => (
    <>
      {isFetchingCampaignResults && (
        <Loader text={t(translations.fetching)} theme="light" cover>
          <Spinner dark />
        </Loader>
      )}
      <Empty
        height="90%"
        width="100%"
        text={t(translations.campaigns_noResultsAvailablePleaseRunASimulationToViewTheResults)}
      />
    </>
  );

  return (
    <>
      <Page left={0} padding={false}>
        {isFetching && (
          <Loader text={t(translations.fetching)} theme="light" cover>
            <Spinner dark />
          </Loader>
        )}
        <Row spacing={0} height="100%">
          <CampaignsList
            campaigns={list}
            onClickDelete={onClickDeleteCampaign}
            campaignModalOpened={campaignModalOpened}
            setActiveTabIndex={setActiveTabIndex}
            activeTabIndex={activeTabIndex}
          />
          {activeCampaign && activeTabIndex > CampaignTabIndex.Input && (
            <CompareEstimatesDrawer estimates={activeCampaign.estimates} filteredEstimates={[]} />
          )}
          {activeCampaign ? (
            <Column scroll padding>
              <Flex justifyContent="space-between" gap>
                <Heading top>
                  {activeCampaign.name} <Spacer width="var(--padding)" />
                  <Text faint>
                    {activeCampaign.estimatesCount ?? activeCampaign.estimates?.length} {t(translations.estimates)}
                  </Text>
                </Heading>
                <Flex gap alignItems="center">
                  <Flex>
                    <Toggle
                      label={t(translations.learningCurve)}
                      checked={activeCampaign.learningCurve}
                      onChange={() => updateLearningCurveFlag(activeCampaign.campaignId)}
                      noMargin
                    />
                    <Button
                      label={t(translations.edit)}
                      onClick={() => learningCurveModalUpdated(true)}
                      small
                      testId={`campaigns-button-${convertToKebabCase(t(translations.edit))}`}
                      basic
                    />
                  </Flex>
                  {activeCampaign?.campaignTotal && (
                    <Button
                      label={t(translations.campaigns_recalculate)}
                      onClick={() => runCampaignTotalSimulation(activeCampaign.campaignId)}
                      small
                      disabled={isRecalculating}
                      loading={isRecalculating}
                      testId={`campaigns-button-${convertToKebabCase(t(translations.campaigns_recalculate))}`}
                    />
                  )}
                </Flex>
              </Flex>
              <Flex justifyContent="right">
                <Message
                  message={{
                    content: isSynced
                      ? t(translations.campaigns_estimatesOutOfSync)
                      : t(translations.campaigns_outOfSync),
                    icon: true,
                    type: 'Warning',
                    visible: isCampaignOutOfSync,
                  }}
                />
              </Flex>
              <Tabs
                testId="campaigns-tabs"
                value={activeTabIndex}
                onChange={handleOnTabChange}
                margin={0}
                options={[
                  {
                    testId: `campaigns-tabs-option-${convertToKebabCase(t(translations.input))}`,
                    value: CampaignTabIndex.Input,
                    label: t(translations.input),
                    disabled: isGeneratingEstimates,
                  },
                  {
                    testId: `campaigns-tabs-option-${convertToKebabCase(t(translations.campaigns_operationsSequence))}`,
                    value: CampaignTabIndex.Operations,
                    label: t(translations.campaigns_operationsSequence),
                  },
                  {
                    testId: `campaigns-tabs-option-${convertToKebabCase(t(translations.result))}`,
                    value: CampaignTabIndex.Result,
                    label: t(translations.result),
                  },
                  ...(showCampaignTimeTracker
                    ? [
                        {
                          value: CampaignTabIndex.TimeTracker,
                          label: t(translations.timeTracker_timeTracker),
                        },
                      ]
                    : []),
                ]}
              />
              {activeTabIndex === CampaignTabIndex.Input && activeCampaign.isFetchingDetails && (
                <Loader text={t(translations.fetching)} theme="light">
                  <Spinner dark />
                </Loader>
              )}
              {activeTabIndex === CampaignTabIndex.Input && !activeCampaign.isFetchingDetails && (
                <CampaignInputTab
                  companyId={company}
                  activeCampaign={activeCampaign}
                  conceptDesigns={conceptDesigns}
                  visibleEstimates={visibleEstimates}
                  setActiveTabIndex={setActiveTabIndex}
                />
              )}
              {renderContent()}
            </Column>
          ) : (
            <Empty height="100%" width="100%" text={t(translations.campaigns_noComparisonSelected)} />
          )}
        </Row>
      </Page>
      {deleteCampaignModalVisible && (
        <DeleteCampaignModal setModalVisible={setDeleteCampaignModalVisible} selectedId={selectedId} />
      )}
      {campaignModalVisible && <CampaignModal campaign={activeCampaign} companyId={company} />}
      {learningCurveModalVisible && activeCampaign && (
        <LearningCurveModal
          campaign={activeCampaign}
          learningCurveModalUpdated={learningCurveModalUpdated}
          probability={probability}
        />
      )}
      {kpiModalVisible && <KpiModal kpiModalUpdated={kpiModalUpdated} campaign={activeCampaign} />}
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const isFetching = selectIsFetchingCampaign(entities);
  const list = selectCampaignList(entities);
  const campaignModalVisible = selectCampaignModalVisible(entities);
  const learningCurveModalVisible = selectLearningCurveModalVisible(entities);
  const kpiModalVisible = selectKpiModalVisible(entities);
  const designs = selectCampaignDesigns(entities);
  const campaignTotal = selectCampaignTotal(entities);
  const isFetchingCampaignResults = selectIsFetchingCampaignResults(entities);
  const isRecalculating = selectIsRecalculatingCampaigns(entities);
  const isGeneratingEstimates = selectIsGeneratingCampaignEstimates(entities);
  const primaryCurrency = selectPrimaryCurrency(entities);
  const activeCampaign = selectActiveCampaign(entities);
  const visibleEstimates = selectActiveEstimates(entities);
  const conceptDesigns = selectConceptDesigns(entities);

  const activeCampaignTotal =
    activeCampaign && campaignTotal ? campaignTotal?.find((item) => item.campaignId === activeCampaign.campaignId) : [];
  return {
    list,
    isFetching,
    activeCampaign,
    campaignModalVisible,
    learningCurveModalVisible,
    kpiModalVisible,
    primaryCurrency,
    designs,
    conceptDesigns,
    visibleEstimates,
    activeCampaignTotal,
    isRecalculating,
    isGeneratingEstimates,
    isFetchingCampaignResults,
  };
};

const mapDispatchToProps = {
  getCampaigns,
  campaignModalOpened,
  learningCurveModalUpdated,
  kpiModalUpdated,
  updateLearningCurveFlag,
  getSimulation,
  addSimulationStatus,
  getCampaignSimulation,
  getCampaignResults,
  campaignsCleanup,
  runCampaignTotalSimulation,
  updateIsRecalculating,
  getCampaignTimeTracker,
  campaignSelected,
  getCampaignDetails,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(Campaigns), {
  style: { position: 'absolute', top: '50px' },
});

export { Container as Campaigns };
